import axios     from 'axios'

import CloudFlyerAPI      from '../../../../api/CloudFlyerAPI'
import {delay, to_hex} from '../../../../utils'
import {message} from "antd";

export class B2FileUploader {

    constructor({
                    file,
                    current_path,
                    cloud,
                    bucket,
                    progress
    }){
        this.file        = file
        this.cloud_id    = cloud
        this.bucket_id   = bucket
        this.on_progress = progress

        this.file_name   = file.fullPath||file.webkitRelativePath||file.name
        this.file_name   = decodeURIComponent(`${current_path === '/' ? '' : current_path}/${this.file_name}`)
        this.file_name   = this.file_name.replace(/^\/+/g, '')
        this.file_name   = this.file_name.replace(/([^:]\/)\/+/g, "$1")
    }

    start ({cancellation_token, retry_count, part_size, max_concurrent_parts}={}) {
        this.part_size            = part_size || 5242880
        this.retry                = retry_count || 3
        this.max_concurrent_parts = max_concurrent_parts || 5
        this.cancel_token         = cancellation_token || axios.CancelToken.source()

        this.cancelled      = false
        this.failed         = false
        this.started        = new Date()
        this.completed_size = 0

        return new Promise(async (resolve, reject) => {
            try {
                this.file.size > this.part_size ?
                    await this.initiate_multipart() :
                    await this.initiate()
                resolve()
            } catch (e) {
                reject(e)
            } finally {
                this.dispose()
            }
        })
    }

    initiate_multipart = () => {
        return new Promise((resolve, reject) =>{
            this.partCount            = Math.ceil(this.file.size / this.part_size) || 1
            this.parts                = Array.from({length: this.partCount}, (_, i) => ({part:++i, retry:0})).reduce((a, b) => ({...a, [b.part]: b}), {})
            this.sha1s                = Array(0)
            this.authorizations       = Array(0)

            return CloudFlyerAPI
                .backblaze
                .initiate_upload(
                    this.cloud_id,
                    this.bucket_id,
                    this.file_name,
                    this.max_concurrent_parts,
                    this.cancel_token.token)
                .then(({data: {fileId, AuthorizationToken, ApiUrl, Parts = []} = {}}) => {

                    this.file.file_id   = fileId
                    this.authorizations = Parts
                    this.upload_parts()
                        .then(resolve)
                        .catch(reject)
                })
                .catch(err => {
                    console.error(err)

                    this.file.message = err.message
                    this.failed = true
                    reject(err)
                })
        })
    }

    initiate = () =>
        new Promise((resolve, reject) =>
            CloudFlyerAPI
                .backblaze
                .upload_url(this.cloud_id, this.bucket_id, this.cancel_token.token)
                .then(({data: {Upload:{token, url} = {}} = {}}) => {
                    this.upload(token, url)
                        .then(resolve)
                        .catch(reject)
                })
                .catch(err => {
                    console.error(err)

                    this.file.message = err.message
                    this.failed       = true
                    reject(err)
                }))

    cancel = () =>
        new Promise(resolve => {
            this.cancelled = true
             CloudFlyerAPI
                .backblaze
                .cancel(this.cloud_id, this.file.file_id)
                .finally(()=>{
                    resolve()
                    this.cancel_token.cancel('Operation canceled by the user.')
                })
        })

    upload_parts = () =>
        new Promise(async (resolve, reject) => {
            while (this.retry !== 0 && !this.cancelled && !this.failed) {
                this.retry--
                while (Object.values(this.parts).find(p=> (!p.sha1 && p.retry !== this.retry && !p.uploading) || p.uploading) && !this.cancelled&& !this.failed) {
                    while (this.authorizations.length && Object.values(this.parts).find(o => !o.sha1 && !o.uploading) && !this.cancelled && !this.failed) {

                        let next_chunk = Object.values(this.parts).find(o => !o.sha1 && !o.uploading)
                        if (!next_chunk) throw new Error('No part available')

                        let auth = this.authorizations.shift()
                        if (!auth) throw new Error('No auth available')

                        this.upload_part(next_chunk.part, {...auth})
                            .finally(() => this.parts[next_chunk.part].retry = this.retry)
                    }
                    await delay(1000)
                }

                if (this.cancelled) return reject(new Error("Upload cancelled"))
                else if (this.failed) return reject(new Error("Upload have failed"))
                else if(!Object.values(this.parts).find(o => !o.sha1)) {
                    try {
                        await this.complete()
                        return resolve(`File ${this.file_name} upload completed successfully`)
                    } catch (e) {
                        return reject(new Error("Upload failed to complete"))
                    }
                }
            }
            reject(new Error('Upload Failed'))
        })

    upload_part = (part, auth) =>
        new Promise(async (resolve, reject) => {

            this.parts[part] = {...this.parts[part], ...{uploading:true}}

            let chunk_size = part === this.partCount ? (this.file.size - this.part_size * (this.partCount - 1)) : this.part_size

            let chunk = {
                part          : part,
                size          : chunk_size,
                start         : (part - 1) * this.part_size,
                end           : (part - 1) * this.part_size + chunk_size,
                authorization : auth.token,
                url           : auth.url,
                cancelToken   : this.cancel_token.token,
                loaded        : 0,
                stream        : '',
                sha1          : '',
                progress      : event => this.report_progress(event, part)
            }

            try {
                chunk.stream = await this.extract_payload(chunk.start, chunk.end)
                chunk.sha1   = to_hex(await window.crypto.subtle.digest('SHA-1', chunk.stream))

                console.log(`chunk uploading ${chunk.part}`)
                await CloudFlyerAPI
                    .backblaze
                    .upload_part(chunk)
                await CloudFlyerAPI
                    .backblaze
                    .verify_part(chunk.part, chunk.size, chunk.sha1, this.cloud_id, this.file.file_id, this.cancel_token.token)

                this.sha1s.push(chunk.sha1)
                this.completed_size += chunk.size
                console.log(`chunk completed ${chunk.part}`)
                resolve(chunk)
            } catch (err) {
                if (typeof err.response === 'undefined') this.failed = true

                console.error(err)
                console.warn(`chunk Failed ${chunk.part}`)

                delete chunk.sha1
                reject(err)
            } finally {
                delete chunk.stream
                this.parts[part] = {...this.parts[part], ...chunk, ...{uploading:false}}
                this.authorizations.push(auth)
            }
        })

    upload = (token, url) =>
        new Promise(async (resolve, reject) => {
            try {
                let sha1     = to_hex(await window.crypto.subtle.digest('SHA-1', await this.read_file()))
                let progress = event => this.report_progress(event)

                console.log(`small file uploading ${this.file_name}`)
                await CloudFlyerAPI
                    .backblaze
                    .upload({url, token, file:this.file, sha1, progress, file_name:this.file_name, cancelToken:this.cancel_token.token})
                console.log(`small file completed ${this.file_name}`)
                resolve()
            } catch (err) {
                if (typeof err.response === 'undefined') this.failed = true

                console.error(err)
                console.warn(`small file upload failed ${this.file_name}`)

                reject(err)
            }
        })

    complete = () => CloudFlyerAPI
        .backblaze
        .finish_upload(
            this.cloud_id,
            this.file.file_id,
            Object.values(this.parts).map(p=> p.sha1),
            this.cancel_token.token
        )

    report_progress = (progressEvent, part) => {
        if (this.cancelled || this.failed) return

        let loaded = progressEvent.loaded
        if (part) {
            this.parts[part].loaded = loaded
            let sum_loaded          = Object.values(this.parts).filter(p=> p.uploading).reduce((x,y) => (x.loaded || 0) + (y.loaded || 0))
            loaded                  = sum_loaded || loaded
        }

        let uploaded    = this.completed_size + loaded
        let time_passed = (new Date() - this.started) / 1000
        let speed       = uploaded / time_passed
        let remaining   = this.file.size - uploaded

        let stats = {
                speed     : speed,
                uploaded  : uploaded,
                remaining : remaining,
                eta       : 0,
                percent   : Math.floor((uploaded * 100) / this.file.size)
            }

        if (speed > 0) stats.time_left = Math.round(remaining / speed)
        this.on_progress(stats)
    }

    extract_payload = (start, end) => new Response(this.file.slice(start, end)).arrayBuffer()

    read_file = () => new Response(this.file).arrayBuffer()

    dispose = () => {
        delete this.file
        delete this.cloud_id
        delete this.bucket_id
        delete this.on_progress
        delete this.cancel_token
        delete this.part_size
        delete this.cancelled
        delete this.failed
        delete this.started
        delete this.completed_size
        delete this.retry
        delete this.max_concurrent_parts
        delete this.partCount
        delete this.parts
        delete this.sha1s
        delete this.authorizations
    }
}

export class B2FolderUploader {

    constructor({
                    dir,
                    current_path,
                    cloud,
                    bucket,
                    progress
                }){

        this.id           = dir.id
        this.current_path = current_path
        this.cloud_id     = cloud
        this.bucket_id    = bucket
        this.on_progress  = progress

        if(dir.type === 'directory' && dir.files){
            this.files = dir.files
        } else if (dir.isDirectory){
            this.dirs = [dir]
        } else throw new Error(`Must be a directory: ${dir.name}`)
    }

    start = ({cancellation_token, retry_count, part_size, max_concurrent_parts, max_concurrent_files}={}) =>{

        this.part_size            = part_size || 5242880
        this.retry_count          = retry_count || 3
        this.max_concurrent_files = max_concurrent_files || 2
        this.max_concurrent_parts = max_concurrent_parts || 5
        this.cancel_token         = cancellation_token || axios.CancelToken.source()

        this.cancelled      = false
        this.failed         = false
        this.started        = new Date()

        this.completed_size  = 0
        this.failed_count    = 0
        this.succeeded_count = 0
        this.concurrency     = Array.from({length: this.max_concurrent_files}, (_, i) => ++i)
        this.progresses      = Array.from({length: this.max_concurrent_files}, (_, i) => ++i).reduce((a, b) => ({...a, [b]: 0}), {})

        return new Promise(async (resolve, reject) => {
            try {
                this.files && this.files.length ?
                    await this.upload_files(this.files) :
                    await this.upload_directories()
                resolve({
                    id              : this.id,
                    failed_count    : this.failed_count,
                    succeeded_count : this.succeeded_count,
                    cancelled       : this.cancelled,
                    failed          : this.failed,
                    started         : this.started,
                    ended           : new Date(),
                    uploaded        : this.completed_size,
                    duration        : (new Date() - this.started)/1000
                })
            } catch (err) {
                this.error = (((err ||{}).response||{}).data||{}).message || err.message || "A network error occurred. This could be a CORS issue or a dropped internet connection."
                reject(err)
            } finally {
                this.on_progress({
                    completed       : true,
                    id              : this.id,
                    uploaded        : this.completed_size,
                    failed_count    : this.failed_count,
                    succeeded_count : this.succeeded_count,
                    cancelled       : this.cancelled,
                    failed          : this.failed,
                    started         : this.started,
                    error           : this.error,
                    duration        : (new Date() - this.started)/1000
                })

                this.dispose()
            }
        })
    }

    upload_directories = () =>{
        return new Promise(async (resolve, reject) => {
            while (this.dirs.length && !this.failed && !this.cancelled) {
                let dir = this.dirs.shift()
                console.log(`Queuing dir ${dir.name}`)
                if (dir) await this.upload_directory(dir)
            }

            if (this.cancelled) {

                this.cancelled = true
                return reject(new Error("Upload cancelled"))
            } else if (this.failed) {

                this.failed = true
                return reject(new Error("Upload have failed"))
            } else {
                try {
                    await this.complete()
                    return resolve(`Directory ${this.id} upload completed successfully`)
                } catch (e){
                    return reject(new Error("Upload failed to complete"))
                }
            }
        })
    }

    upload_files = files =>{
        return new Promise(async (resolve, reject) => {
            while (files.length && !this.failed && !this.cancelled){
                while (this.concurrency.length && !this.failed && !this.cancelled) {
                    let next_file = files.shift()
                    if (!next_file) throw new Error('No file available')
                    if(next_file.name.startsWith('.'))continue

                    let concurrency = this.concurrency.shift()
                    if (!concurrency) throw new Error('No concurrency token available')

                    if (next_file.file) next_file.file(file =>
                        next_file.getMetadata(m => {
                            file.fullPath = next_file.fullPath
                            return this.upload_file(file, concurrency)
                                .then(() => {
                                    this.succeeded_count++
                                    this.completed_size += file.size
                                })
                                .catch(() => this.failed_count++)
                                .finally(() => this.concurrency.push(concurrency))
                        }))
                    else this.upload_file(next_file, concurrency)
                        .then(() => {
                            this.succeeded_count++
                            this.completed_size += next_file.size
                        })
                        .catch(() => this.failed_count++)
                        .finally(() => this.concurrency.push(concurrency))
                }
                await delay(1000)
            }

            if (this.cancelled) return reject(new Error("Upload cancelled"))
            if (this.failed) return reject(new Error("Upload have failed"))
            return resolve(`${files.length} files upload completed successfully`)
        })
    }

    upload_directory = dir_entry => {
        let dir      = dir_entry
        const reader = dir.createReader()
        return new Promise((resolve, reject) => {
            let readEntries =()=> {
                reader.readEntries(async entries => {
                    if (entries.length) {
                        try {
                            await this.upload_files(entries.filter(e=> e.isFile))
                        }catch (e) {
                            // retry logic ?
                        }
                        this.dirs.push(...entries.filter(e=> e.isDirectory))
                        readEntries()
                    } else {
                        resolve()
                    }
                }, error => reject(error))
            }
            readEntries()
        })
    }

    upload_file = (file, progress_id) =>
        new Promise(async (resolve, reject) => {
            try {
                await new B2FileUploader({
                    file,
                    current_path : this.current_path,
                    cloud        : this.cloud_id,
                    bucket       : this.bucket_id,
                    progress     : stat => this.report_progress(progress_id, stat)
                })
                    .start({
                        cancellation_token   : this.cancel_token,
                        retry_count          : this.retry_count,
                        part_size            : this.part_size,
                        max_concurrent_parts : this.max_concurrent_parts
                    })
                resolve(file)
            } catch (err) {
                console.error(err)

                debugger

                let error = (((err ||{}).response||{}).data||{}).message || err.message || "A network error occurred. This could be a CORS issue or a dropped internet connection."
                console.info('File did not upload successfully:', error)

                file.$status   = 'exception'
                file.$errorMsg = error
                file.$error    = error

                reject(err)
            }
        })

    report_progress = (progress_id, file_progress) => {
        if (this.cancelled || this.failed) return

        this.progresses[progress_id] = file_progress.uploaded
        let loaded                   = Object.values(this.progresses).reduce((x,y) => (x||0) + (y||0))||0

        let uploaded                 = this.completed_size + loaded
        let time_passed              = (new Date() - this.started) / 1000
        let speed                    = uploaded / time_passed

        let stats = {
            started         : this.started,
            speed           : speed,
            uploaded        : uploaded,
            failed_count    : this.failed_count,
            succeeded_count : this.succeeded_count
        }
        this.on_progress(stats)
    }

    cancel = () =>
        new Promise(resolve => {
            this.cancelled = true
            this.cancel_token.cancel('Operation canceled by the user.')
            resolve()
        })

    complete = () =>{
        return new Promise(async resolve => {
            while (!this.failed && !this.cancelled && this.concurrency.length !== this.max_concurrent_files) {
                await delay(1000)
            }
            resolve()
        })
    }

    dispose = () => {
        delete this.dirs
        delete this.max_concurrent_files
        delete this.failed_count
        delete this.succeeded_count
        delete this.concurrency
        delete this.id
        delete this.current_path
        delete this.cloud_id
        delete this.bucket_id
        delete this.on_progress
        delete this.cancel_token
        delete this.part_size
        delete this.cancelled
        delete this.failed
        delete this.started
        delete this.completed_size
        delete this.retry_count
        delete this.max_concurrent_parts
        delete this.progresses
    }
}